// Headless reads for the order page of a Tharstern-era order of one or many items (doc 48): the numbers that name an
// item, the one line each says about itself, and the carrier's word laid over the Delivery tab the server served
// (`delivery_groups`; its consignments' progress is read lazily).
// Pure, so the page stays layout. Every sentence about work in progress is the read's (the job's own state phrase,
// the backend's status line); the order's banner and invoicing words are the backend's too, printed as given.

import type {
  CustomerOrderDeliveryGroup,
  CustomerStepperStop,
  CustomerOrderItem,
  CustomerOrderWithItems,
} from "../data/customer-api-types";
import { formatCount } from "./formatters";
import type { ConsignmentProgressByKey } from "./useOrderConsignmentProgress";
import {
  applyCarrierProgress,
  carrierProgress,
  finishedFacts,
  hasFinishedAllStops,
  stopLabel,
  visibleStops,
  type CarrierProgress,
  type FinishedFacts,
} from "./customerOrderDetail";

/** The customer's item number, "01": an item's place in its order. */
export function itemRef(index: number): string {
  return String(index + 1).padStart(2, "0");
}

export const pence = (amount: string | null | undefined): number =>
  Math.round(Number(amount ?? 0) * 100);

/**
 * Where an item's goods are, once its work is finished, by the one rule for it (`finishedFacts`, #2646): "Delivered"
 * only when the carrier confirms it or Tharstern has a completed date, "Collected" for a collection, "Despatched" once
 * a quantity has gone, and null (still ours) otherwise: every stop done says the work is finished, not that the goods
 * have gone. A stock call-off is supplied from stock. `stops` are the job's stops as the carrier says they stand
 * (`applyCarrierProgress`); `carrier` is the parcel timeline's progress when it has spoken, which the list
 * has not (it is a lazy read for the item on show).
 */
export function itemOutcome(
  item: CustomerOrderItem,
  stops: CustomerStepperStop[] = item.progress?.stepper ?? [],
  carrier: CarrierProgress | null = null,
): FinishedFacts | null {
  if (item.is_call_off) {
    return {
      tone: "delivered",
      headline: "Supplied from stock",
      dateLabel: "Supplied on",
      date: item.delivery?.delivered_on ?? null,
    };
  }
  if (item.progress) {
    if (!hasFinishedAllStops(stops)) return null;
    const shown = visibleStops(stops);
    return finishedFacts(
      item.delivery,
      shown[shown.length - 1]?.actual_at ?? null,
      carrier,
    );
  }
  // No tracker: the read's own status line is all there is, and it says Despatched or Delivered the same way.
  return item.status_line === "Delivered" || item.status_line === "Despatched"
    ? finishedFacts(item.delivery, null, carrier)
    : null;
}

/**
 * The carrier's word per item (`carrierProgress` of its parcel timeline), by `order_ref`, for the items whose
 * timeline has been read. The timeline is a lazy read for the item on show, so the list and the order's
 * "Complete" know the carrier's word only for those, and say what Tharstern says for the rest.
 */
export type CarrierByItem = Readonly<Record<string, CarrierProgress | null>>;

/** An item's outcome with the carrier's word where it is known: its stops follow the carrier, and a delivery it confirms counts. */
function outcomeWith(
  item: CustomerOrderItem,
  carriers: CarrierByItem,
): FinishedFacts | null {
  const carrier = carriers[item.order_ref] ?? null;
  const stops = item.progress
    ? applyCarrierProgress(
        item.progress.stepper ?? [],
        carrier,
        item.delivery?.delivered_on,
      )
    : [];
  return itemOutcome(item, stops, carrier);
}

/** Whether an item's goods have gone from us (delivered, collected, supplied or despatched): it waits on nothing of ours. */
export function hasLeftUs(
  item: CustomerOrderItem,
  carriers: CarrierByItem = {},
): boolean {
  return outcomeWith(item, carriers) !== null;
}

/** Whether an item has arrived: delivered, collected or supplied from stock. A despatched item has not, as far as we know. */
export function isItemDelivered(
  item: CustomerOrderItem,
  carriers: CarrierByItem = {},
): boolean {
  return outcomeWith(item, carriers)?.tone === "delivered";
}

/** What an item in the list says about itself, in one line: what the customer must do, else where it is up to. */
export function itemListStatus(
  item: CustomerOrderItem,
  carriers: CarrierByItem = {},
): string {
  if (item.awaiting_customer) return item.awaiting_customer;
  const outcome = outcomeWith(item, carriers);
  if (outcome) return outcome.headline;
  // Every stop done with nothing to say it has gone reads as the last stop's own words ("Ready to send").
  const stops = item.progress?.stepper ?? [];
  if (item.progress && hasFinishedAllStops(stops)) {
    const shown = visibleStops(stops);
    return stopLabel(shown[shown.length - 1]);
  }
  return item.progress?.current_state_phrase ?? item.status_line;
}

/** The "next step" line under an item's title: where it is up to, or what it waits on. */
export function itemNextStep(item: CustomerOrderItem): string | null {
  if (item.awaiting_customer) return item.awaiting_customer;
  if (item.is_call_off) return "Supplied from your stock";
  // Delivered, collected: nothing is next. Despatched keeps the job's own phrase (it says Despatched).
  if (itemOutcome(item)?.tone === "delivered") return null;
  return item.progress?.current_state_phrase ?? "We've got your order";
}

/** The size, paper and print of an item on one line (the first three spec values), for under its title. */
export function itemSpecLine(item: CustomerOrderItem): string {
  return (item.spec ?? [])
    .slice(0, 3)
    .map((row) => row.value)
    .join(", ");
}

/**
 * The carrier's word per item, from the consignment progress the page has read: an item's next step, list status and
 * the order's "Complete" follow the consignment its delivery names (`delivery.consignment_key`, its latest despatched
 * note). An item whose consignment is unread, failed or says nothing keeps what Tharstern says.
 */
export function carriersByItem(
  order: CustomerOrderWithItems,
  progress: ConsignmentProgressByKey,
): CarrierByItem {
  const carriers: Record<string, CarrierProgress | null> = {};
  for (const item of order.items) {
    const key = item.delivery?.consignment_key;
    const read = key ? progress[key] : undefined;
    if (read?.status === "ready") {
      carriers[item.order_ref] = carrierProgress(read.answer.progress);
    }
  }
  return carriers;
}

/**
 * The order's delivery groups with each consignment's own read laid over what the order read served: its `day` and
 * `day_tone` where the progress gives one, and its milestones where it has any to show. A consignment not yet read
 * (or one that failed) stays as served.
 */
export function withConsignmentProgress(
  groups: CustomerOrderDeliveryGroup[],
  progress: ConsignmentProgressByKey,
): CustomerOrderDeliveryGroup[] {
  return groups.map((group) => ({
    ...group,
    consignments: group.consignments.map((consignment) => {
      const read = consignment.key ? progress[consignment.key] : undefined;
      if (read?.status !== "ready") return consignment;
      const { day, day_tone, progress: timeline } = read.answer;
      return {
        ...consignment,
        day: day ?? consignment.day,
        day_tone: day ? day_tone : consignment.day_tone,
        progress:
          timeline && timeline.events.length > 0
            ? { events: timeline.events, as_at: timeline.as_at ?? null }
            : consignment.progress,
      };
    }),
  }));
}

/**
 * The sentence for the Delivery tab and the page's header: what the undelivered consignments say when they agree,
 * else the first that needs the customer's eye, else the first. Null while none has a sentence yet (the order's own
 * banner then stands). The server words each consignment's sentence; this only chooses among them.
 */
export function pressingConsignmentSentence(
  groups: CustomerOrderDeliveryGroup[],
): { text: string; tone: "normal" | "late" } | null {
  const open = groups
    .flatMap((group) => group.consignments)
    .filter((c) => c.day && c.day_tone !== "good");
  if (open.length === 0) return null;
  const chosen =
    new Set(open.map((c) => c.day)).size === 1
      ? open[0]
      : (open.find((c) => c.day_tone === "attention") ?? open[0]);
  return {
    text: chosen.day ?? "",
    tone: chosen.day_tone === "attention" ? "late" : "normal",
  };
}

/** When the order is needed by: the latest date any item is due. */
export function orderNeededBy(order: CustomerOrderWithItems): string | null {
  const dates = order.items
    .map((item) => item.progress?.required_at ?? item.expected_at)
    .filter((d): d is string => Boolean(d))
    .sort();
  return dates[dates.length - 1] ?? null;
}

export interface ItemDateFact {
  label: string;
  /** ISO date or timestamp. */
  date: string;
  tone: "normal" | "late" | "done";
}

/**
 * The one date an item's details give: when it was delivered, collected, supplied or despatched (by the one rule,
 * `finishedFacts`), when it was due if it is running late, else when it is expected. `stops` are the job's stops as the carrier says they stand (`applyCarrierProgress`). Null when
 * nothing dates it.
 */
export function itemDateFact(
  item: CustomerOrderItem,
  stops: CustomerStepperStop[],
  carrier: CarrierProgress | null = null,
): ItemDateFact | null {
  const outcome = itemOutcome(item, stops, carrier);
  if (outcome) {
    // Delivered, Collected, Supplied or Despatched on the date the one rule gives; undated says nothing.
    return outcome.date
      ? { label: outcome.dateLabel, date: outcome.date, tone: "done" }
      : null;
  }
  const delivery = item.delivery;
  const isCollection = delivery?.method === "collection";
  const due =
    item.progress?.required_at ?? delivery?.expected_on ?? item.expected_at;
  if (!due) return null;
  if (item.progress?.rag === "red")
    return { label: "Was due", date: due, tone: "late" };
  return {
    label: isCollection ? "Ready to collect" : "Expected delivery",
    date: due,
    tone: "normal",
  };
}

/**
 * An order's title. Tharstern gives an order none, so it is its items' names, kept short: one item is its name, two
 * are "A and B", more are "A and 2 more".
 */
export function orderTitle(items: CustomerOrderItem[]): string {
  const names = items.map((i) => i.description);
  if (names.length <= 2) return names.join(" and ");
  return `${names[0]} and ${formatCount(names.length - 1)} more`;
}
